import type { IsoDate } from './types.js';

/** Calendar-date (ISO `YYYY-MM-DD`) ↔ unix-seconds-UTC conversions at day boundaries. */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export class InvalidIsoDateError extends Error {
  override readonly name = 'InvalidIsoDateError';
  constructor(value: string) {
    super(`Invalid ISO date: ${value}`);
  }
}

export class InvalidUnixTimestampError extends Error {
  override readonly name = 'InvalidUnixTimestampError';
  constructor(unix: number) {
    super(`Unix timestamp outside ISO years 0-9999: ${unix}`);
  }
}

export function buildTrailingTwelveMonthCutoffs(asOfDate: string): readonly IsoDate[] {
  isoToUnix(asOfDate);
  const [year, month] = asOfDate.split('-').map(Number) as [number, number, number];
  const cutoffs: IsoDate[] = [];
  for (let offset = 11; offset >= 1; offset -= 1) {
    const monthIndex = month - 1 - offset;
    const cutoffYear = year + Math.floor(monthIndex / 12);
    const zeroBasedMonth = ((monthIndex % 12) + 12) % 12;
    const day = new Date(Date.UTC(cutoffYear, zeroBasedMonth + 1, 0)).getUTCDate();
    cutoffs.push(
      `${String(cutoffYear).padStart(4, '0')}-${String(zeroBasedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    );
  }
  cutoffs.push(asOfDate as IsoDate);
  return cutoffs;
}

export function unixToIsoDate(unix: number): IsoDate {
  const date = new Date(unix * 1000);
  if (Number.isNaN(date.getTime())) throw new InvalidUnixTimestampError(unix);
  const year = date.getUTCFullYear();
  if (year < 0 || year > 9999) throw new InvalidUnixTimestampError(unix);
  return date.toISOString().slice(0, 10);
}

function partsAt(unix: number, timeZone: string): [number, number, number, number, number, number] {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(unix * 1000));
  const value = (type: 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second'): number =>
    Number(parts.find((part) => part.type === type)?.value);
  return [
    value('year'),
    value('month'),
    value('day'),
    value('hour'),
    value('minute'),
    value('second'),
  ];
}

export function unixToIsoDateInTimeZone(unix: number, timeZone: string): IsoDate {
  const [year, month, day] = partsAt(unix, timeZone);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function offsetSecondsAt(unix: number, timeZone: string): number {
  const [year, month, day, hour, minute, second] = partsAt(unix, timeZone);
  return Date.UTC(year, month - 1, day, hour, minute, second) / 1000 - unix;
}

export interface IsoToUnixOptions {
  /** Resolve to the last second of the day (23:59:59Z) instead of midnight. */
  readonly endOfDay?: boolean;
}

export function isoToUnix(iso: string, options: IsoToUnixOptions = {}): number {
  if (!ISO_DATE_RE.test(iso)) throw new InvalidIsoDateError(iso);
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  const ms = Date.UTC(year, month - 1, day);
  const roundTrip = new Date(ms);
  if (roundTrip.getUTCMonth() !== month - 1 || roundTrip.getUTCDate() !== day) {
    throw new InvalidIsoDateError(iso);
  }
  const endOfDayOffset = options.endOfDay ? 86399 : 0;
  return Math.floor(ms / 1000) + endOfDayOffset;
}

export function isoDateBoundaryToUnix(
  iso: string,
  timeZone: string,
  boundary: 'start' | 'end',
): number {
  isoToUnix(iso);
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  const desired =
    boundary === 'start'
      ? ([year, month, day, 0, 0, 0] as const)
      : ([year, month, day, 23, 59, 59] as const);
  const wallUtc =
    Date.UTC(desired[0], desired[1] - 1, desired[2], desired[3], desired[4], desired[5]) / 1000;
  let candidate = wallUtc - offsetSecondsAt(wallUtc, timeZone);
  candidate = wallUtc - offsetSecondsAt(candidate, timeZone);
  const actual = partsAt(candidate, timeZone);
  if (actual.every((value, index) => value === desired[index])) {
    return candidate;
  }
  // The wall-clock time does not exist in this zone (DST spring-forward gap):
  // fall back to the local-day frontier — first second of the day for 'start',
  // last second for 'end'. Local date is monotonic in unix, so binary-search it.
  return localDayFrontier(iso, timeZone, boundary, wallUtc);
}

function localDayFrontier(
  iso: string,
  timeZone: string,
  boundary: 'start' | 'end',
  wallUtc: number,
): number {
  let low = wallUtc - 2 * 86_400;
  let high = wallUtc + 2 * 86_400;
  if (unixToIsoDateInTimeZone(low, timeZone) >= iso) throw new InvalidIsoDateError(iso);
  if (unixToIsoDateInTimeZone(high, timeZone) <= iso) throw new InvalidIsoDateError(iso);
  if (boundary === 'start') {
    // Smallest t with localDate(t) >= iso.
    while (low + 1 < high) {
      const mid = Math.floor((low + high) / 2);
      if (unixToIsoDateInTimeZone(mid, timeZone) >= iso) high = mid;
      else low = mid;
    }
    return high;
  }
  // Largest t with localDate(t) <= iso.
  while (low + 1 < high) {
    const mid = Math.floor((low + high) / 2);
    if (unixToIsoDateInTimeZone(mid, timeZone) <= iso) low = mid;
    else high = mid;
  }
  return low;
}

export function isValidIsoDate(iso: string): boolean {
  try {
    isoToUnix(iso);
    return true;
  } catch {
    return false;
  }
}
